import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { MaintenanceData, MaintenanceDeleteRequest, Person, Tag, Role, Language, UnusedSeries, UnusedImage, ExportStatusResponse, ImportStatusResponse } from '../../types';
import { getMaintenanceData, deleteUnusedEntities, exportCollection, downloadExport, importCollection, getImageUrl } from '../../api';

interface MaintenanceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComicsUpdated?: () => void;
}

const MaintenanceModal: React.FC<MaintenanceModalProps> = ({ isOpen, onClose, onComicsUpdated }) => {
  const { t } = useTranslation();
  const [maintenanceData, setMaintenanceData] = useState<MaintenanceData | null>(null);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [selectedItems, setSelectedItems] = useState({
    people: new Set<number>(),
    tags: new Set<number>(),
    roles: new Set<number>(),
    languages: new Set<number>(),
    images: new Set<string>(),
  });
  const [deleteResult, setDeleteResult] = useState<string | null>(null);
  
  // Export/Import state
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [exportOptions, setExportOptions] = useState({
    include_images: true,
    include_custom_lists: true,
  });
  const [importOptions, setImportOptions] = useState({
    overwrite_existing: false,
    import_images: true,
    import_custom_lists: true,
  });
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importResult, setImportResult] = useState<ImportStatusResponse | null>(null);
  const [exportResult, setExportResult] = useState<ExportStatusResponse | null>(null);
  
  // Image tooltip state
  const [hoveredImage, setHoveredImage] = useState<string | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    if (isOpen) {
      loadMaintenanceData();
      setDeleteResult(null);
      setImportResult(null);
      setExportResult(null);
      setImportFile(null);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen && !deleting) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, onClose, deleting]);

  const loadMaintenanceData = async () => {
    setLoading(true);
    try {
      const data = await getMaintenanceData();
      setMaintenanceData(data);
      // Reset selections when new data is loaded
      setSelectedItems({
        people: new Set(),
        tags: new Set(),
        roles: new Set(),
        languages: new Set(),
        images: new Set(),
      });
      // Clear image tooltip state when reloading data
      setHoveredImage(null);
    } catch (error) {
      
    } finally {
      setLoading(false);
    }
  };

  const toggleSelection = (type: keyof typeof selectedItems, id: number | string) => {
    setSelectedItems(prev => {
      if (type === 'images') {
        const newSet = new Set(prev[type] as Set<string>);
        if (newSet.has(id as string)) {
          newSet.delete(id as string);
        } else {
          newSet.add(id as string);
        }
        return { ...prev, [type]: newSet };
      } else {
        const newSet = new Set(prev[type] as Set<number>);
        if (newSet.has(id as number)) {
          newSet.delete(id as number);
        } else {
          newSet.add(id as number);
        }
        return { ...prev, [type]: newSet };
      }
    });
  };

  const selectAll = (type: keyof typeof selectedItems, items: any[]) => {
    setSelectedItems(prev => {
      if (type === 'images') {
        const newSet = new Set(prev[type] as Set<string>);
        items.forEach(item => newSet.add(item.filename));
        return { ...prev, [type]: newSet };
      } else {
        const newSet = new Set(prev[type] as Set<number>);
        items.forEach(item => newSet.add(item.id));
        return { ...prev, [type]: newSet };
      }
    });
  };

  const deselectAll = (type: keyof typeof selectedItems) => {
    setSelectedItems(prev => ({ ...prev, [type]: new Set() }));
  };

  const handleDelete = async () => {
    if (!maintenanceData) return;

    const totalSelected = Object.values(selectedItems).reduce<number>((sum, set) => sum + set.size, 0);
    if (totalSelected === 0) {
      alert(t('maintenance.selectAtLeastOneItem'));
      return;
    }

    if (!window.confirm(t('maintenance.confirmDelete', { count: totalSelected }))) {
      return;
    }

    setDeleting(true);
    try {
      const request: MaintenanceDeleteRequest = {
        people_ids: Array.from(selectedItems.people),
        tag_ids: Array.from(selectedItems.tags),
        role_ids: Array.from(selectedItems.roles),
        language_ids: Array.from(selectedItems.languages),
        image_filenames: Array.from(selectedItems.images),
      };

      const result = await deleteUnusedEntities(request);
      setDeleteResult(t('maintenance.deleteSuccess', { 
        total: result.total_deleted,
        people: result.deleted_people,
        tags: result.deleted_tags,
        roles: result.deleted_roles,
        languages: result.deleted_languages,
        images: result.deleted_images
      }));
      
      // Reload data to show updated state
      await loadMaintenanceData();
    } catch (error) {
      
      setDeleteResult(t('maintenance.deleteFailed'));
    } finally {
      setDeleting(false);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const handleImageMouseEnter = (filename: string, event: React.MouseEvent) => {
    setHoveredImage(filename);
    setTooltipPosition({ x: event.clientX, y: event.clientY });
  };

  const handleImageMouseLeave = () => {
    setHoveredImage(null);
  };

  const handleImageMouseMove = (event: React.MouseEvent) => {
    if (hoveredImage) {
      setTooltipPosition({ x: event.clientX, y: event.clientY });
    }
  };

  // Export/Import handlers
  const handleExport = async () => {
    setExporting(true);
    setExportResult(null);
    try {
      const result = await exportCollection(exportOptions);
      setExportResult(result);
      
      if (result.status === 'completed' && result.download_url) {
        // Extract filename from download URL
        const filename = result.download_url.split('/').pop();
        if (filename) {
          // Trigger download
          const blob = await downloadExport(filename);
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = filename;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        }
      }
    } catch (error) {
      
      setExportResult({
        status: 'failed',
        message: t('maintenance.exportFailed'),
        created_at: new Date().toISOString(),
      });
    } finally {
      setExporting(false);
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      alert(t('maintenance.selectFileToImport'));
      return;
    }

    if (!window.confirm(t('maintenance.confirmImport'))) {
      return;
    }

    setImporting(true);
    setImportResult(null);
    try {
      const result = await importCollection(
        importFile,
        importOptions.overwrite_existing,
        importOptions.import_images,
        importOptions.import_custom_lists
      );
      setImportResult(result);
      
      if (result.status === 'completed') {
        // Reload maintenance data to show updated state
        await loadMaintenanceData();
        // Notify parent component that comics have been updated
        if (onComicsUpdated) {
          onComicsUpdated();
        }
      }
    } catch (error) {
      
      setImportResult({
        status: 'failed',
        message: t('maintenance.importFailed'),
        imported_comics: 0,
        imported_people: 0,
        imported_tags: 0,
        imported_languages: 0,
        imported_custom_lists: 0,
        errors: ['Import failed. Please try again.'],
      });
    } finally {
      setImporting(false);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.name.endsWith('.zip')) {
        alert(t('maintenance.selectZipFile'));
        return;
      }
      setImportFile(file);
      setImportResult(null);
    }
  };

  if (!isOpen) return null;

  const EntitySection = ({ 
    title, 
    items, 
    type, 
    renderItem 
  }: { 
    title: string, 
    items: any[], 
    type: keyof typeof selectedItems,
    renderItem: (item: any) => React.ReactNode 
  }) => (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-md font-medium text-gray-900 dark:text-gray-100">{title} ({items.length})</h4>
        {items.length > 0 && (
          <div className="space-x-2">
            <button
              onClick={() => selectAll(type, items)}
              className="text-xs text-blue-600 hover:text-blue-800"
            >
              {t('maintenance.selectAll')}
            </button>
            <button
              onClick={() => deselectAll(type)}
              className="text-xs text-gray-600 hover:text-gray-800"
            >
              {t('maintenance.deselectAll')}
            </button>
          </div>
        )}
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400 italic">{t('maintenance.noUnusedFound', { type: title.toLowerCase() })}</p>
      ) : (
        <div className="max-h-32 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded p-2 bg-gray-50 dark:bg-gray-700">
          {items.map((item, index) => (
            <div key={index} className="flex items-center space-x-2 py-1">
              <input
                type="checkbox"
                checked={type === 'images' ? 
                  (selectedItems[type] as Set<string>).has(item.filename) :
                  (selectedItems[type] as Set<number>).has(item.id)
                }
                onChange={() => toggleSelection(type, type === 'images' ? item.filename : item.id)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded"
              />
              {renderItem(item)}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="fixed inset-0 bg-gray-600 dark:bg-gray-900 bg-opacity-50 dark:bg-opacity-75 overflow-y-auto h-full w-full z-50">
      <div className="relative top-4 mx-auto p-5 border border-gray-200 dark:border-gray-700 w-4/5 max-w-4xl shadow-lg rounded-md bg-white dark:bg-gray-800 max-h-[90vh] overflow-y-auto">
        <div className="mt-3">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">{t('maintenance.databaseMaintenance')}</h3>
            <button
              onClick={loadMaintenanceData}
              disabled={loading}
              className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md disabled:opacity-50"
            >
              {loading ? t('common.refreshing') : t('maintenance.refresh')}
            </button>
          </div>

          {deleteResult && (
            <div className={`mb-4 p-3 rounded-md ${deleteResult.includes('Successfully') ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
              {deleteResult}
            </div>
          )}

          {loading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-2 text-gray-600">{t('maintenance.loadingMaintenanceData')}</p>
            </div>
          ) : maintenanceData ? (
            <div className="space-y-6">
              {/* Export/Import Section */}
              <div className="border-b border-gray-200 pb-6">
                <h4 className="text-md font-medium text-gray-900 dark:text-gray-100 mb-4">{t('maintenance.exportCollection')} / {t('maintenance.importCollection')}</h4>
                
                {/* Export Section */}
                <div className="mb-6">
                  <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('maintenance.exportCollection')}</h5>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">{t('maintenance.exportDescription')}</p>
                  
                  <div className="flex flex-wrap items-center gap-4 mb-3">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={exportOptions.include_images}
                        onChange={(e) => setExportOptions(prev => ({ ...prev, include_images: e.target.checked }))}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">{t('maintenance.includeCoverImages')}</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={exportOptions.include_custom_lists}
                        onChange={(e) => setExportOptions(prev => ({ ...prev, include_custom_lists: e.target.checked }))}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">{t('maintenance.includeCustomLists')}</span>
                    </label>
                  </div>
                  
                  <button
                    onClick={handleExport}
                    disabled={exporting}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50"
                  >
                    {exporting ? t('common.exporting') : t('maintenance.exportCollection')}
                  </button>
                  
                  {exportResult && (
                    <div className={`mt-2 p-2 rounded-md text-sm ${
                      exportResult.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {exportResult.message}
                    </div>
                  )}
                </div>

                {/* Import Section */}
                <div>
                  <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('maintenance.importCollection')}</h5>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">{t('maintenance.importDescription')}</p>
                  
                  <div className="mb-3">
                    <input
                      type="file"
                      accept=".zip"
                      onChange={handleFileSelect}
                      className="block w-full text-sm text-gray-500 dark:text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 dark:file:bg-blue-900 file:text-blue-700 dark:file:text-blue-200 hover:file:bg-blue-100 dark:hover:file:bg-blue-800"
                    />
                    {importFile && (
                      <p className="text-xs text-gray-600 dark:text-gray-300 mt-1">{t('maintenance.selectedFile', { filename: importFile.name })}</p>
                    )}
                  </div>
                  
                  <div className="flex flex-wrap items-center gap-4 mb-3">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={importOptions.overwrite_existing}
                        onChange={(e) => setImportOptions(prev => ({ ...prev, overwrite_existing: e.target.checked }))}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">{t('maintenance.overwriteExistingItems')}</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={importOptions.import_images}
                        onChange={(e) => setImportOptions(prev => ({ ...prev, import_images: e.target.checked }))}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">{t('maintenance.importCoverImages')}</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={importOptions.import_custom_lists}
                        onChange={(e) => setImportOptions(prev => ({ ...prev, import_custom_lists: e.target.checked }))}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">{t('maintenance.importCustomLists')}</span>
                    </label>
                  </div>
                  
                  <button
                    onClick={handleImport}
                    disabled={importing || !importFile}
                    className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {importing ? t('common.importing') : t('maintenance.importCollection')}
                  </button>
                  
                  {importResult && (
                    <div className={`mt-2 p-2 rounded-md text-sm ${
                      importResult.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      <div>{importResult.message}</div>
                      {importResult.status === 'completed' && (
                        <div className="text-xs mt-1">
                          {t('maintenance.importResults', {
                            comics: importResult.imported_comics,
                            people: importResult.imported_people,
                            tags: importResult.imported_tags,
                            languages: importResult.imported_languages,
                            lists: importResult.imported_custom_lists
                          })}
                        </div>
                      )}
                      {importResult.errors.length > 0 && (
                        <div className="text-xs mt-1">
                          <div className="font-medium">{t('maintenance.importErrors')}</div>
                          {importResult.errors.map((error, index) => (
                            <div key={index}>• {error}</div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Cleanup Section */}
              <div>
                <h4 className="text-md font-medium text-gray-900 dark:text-gray-100 mb-4">{t('maintenance.databaseCleanup')}</h4>
                <p className="text-sm text-gray-600 mb-4">
                  {t('maintenance.cleanupDescription')}
                </p>

              <EntitySection
                title={t('maintenance.people')}
                items={maintenanceData.unused_people}
                type="people"
                renderItem={(person: Person) => (
                  <span className="text-sm text-gray-700 dark:text-gray-300">{person.name}</span>
                )}
              />

              <EntitySection
                title={t('maintenance.tags')}
                items={maintenanceData.unused_tags}
                type="tags"
                renderItem={(tag: Tag) => (
                  <span className="text-sm text-gray-700 dark:text-gray-300">{tag.name}</span>
                )}
              />

              <EntitySection
                title={t('maintenance.roles')}
                items={maintenanceData.unused_roles}
                type="roles"
                renderItem={(role: Role) => (
                  <span className="text-sm text-gray-700 dark:text-gray-300">{role.name}</span>
                )}
              />


              <EntitySection
                title={t('maintenance.languages')}
                items={maintenanceData.unused_languages}
                type="languages"
                renderItem={(language: Language) => (
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    {language.name} {language.code && `(${language.code})`}
                  </span>
                )}
              />


              <div className="mb-6">
                <h4 className="text-md font-medium text-gray-900 dark:text-gray-100 mb-2">
                  {t('maintenance.singleUseSeries', { count: maintenanceData.unused_series.length })}
                </h4>
                {maintenanceData.unused_series.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400 italic">{t('maintenance.noSingleUseSeriesFound')}</p>
                ) : (
                  <div className="max-h-32 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded p-2 bg-gray-50 dark:bg-gray-700">
                    {maintenanceData.unused_series.map((series: UnusedSeries, index) => (
                      <div key={index} className="py-1">
                        <span className="text-sm text-gray-700 dark:text-gray-300">{series.series}</span>
                        <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">{t('maintenance.singleComicNote', { count: series.count })}</span>
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {t('maintenance.seriesNote')}
                </p>
              </div>

              <EntitySection
                title={t('maintenance.coverImages')}
                items={maintenanceData.unused_images}
                type="images"
                renderItem={(image: UnusedImage) => (
                  <span 
                    className="text-sm text-gray-700 dark:text-gray-300 cursor-pointer hover:text-blue-600"
                    onMouseEnter={(e) => handleImageMouseEnter(image.filename, e)}
                    onMouseLeave={handleImageMouseLeave}
                    onMouseMove={handleImageMouseMove}
                  >
                    {image.filename} ({formatFileSize(image.size)})
                  </span>
                )}
              />

              <div className="flex justify-between items-center pt-4 border-t">
                <div className="text-sm text-gray-600">
                  {t('maintenance.selectedItems', { count: Object.values(selectedItems).reduce<number>((sum, set) => sum + set.size, 0) })}
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={onClose}
                    disabled={deleting}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md disabled:opacity-50"
                  >
                    {t('common.close')}
                  </button>
                  <button
                    onClick={handleDelete}
                    disabled={deleting || Object.values(selectedItems).every(set => set.size === 0)}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {deleting ? t('common.deleting') : t('maintenance.deleteSelected')}
                  </button>
                </div>
              </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-600">
              {t('maintenance.failedToLoadData')}
            </div>
          )}
        </div>
        
        {/* Image Tooltip */}
        {hoveredImage && (
          <div 
            className="fixed z-50 pointer-events-none"
            style={{
              left: tooltipPosition.x + 10,
              top: tooltipPosition.y - 140,
              maxWidth: '300px'
            }}
          >
            <div className="bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg p-2">
              <img
                src={getImageUrl(hoveredImage)}
                alt={hoveredImage}
                className="w-full h-64 object-contain"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                }}
              />
              <div className="text-xs text-gray-600 text-center mt-1">
                {hoveredImage}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MaintenanceModal;